import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, User, CheckCircle, ArrowRight, Shield, Users, FileText, Video, Loader2, MessageCircle } from 'lucide-react';
import { captureLead } from '../../api/client';
import { trackLeadFunnel } from '../../hooks/useTracking';
import Input from '../ui/Input';

const BENEFITS = [
  { icon: FileText, text: 'AI Finance Starter Guide (PDF)' },
  { icon: Video, text: 'Free Masterclass Recording' },
  { icon: Users, text: 'Join 2,000+ Professionals' },
];

export default function LeadCapturePopup(): React.JSX.Element | null {
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Scroll-based trigger: show when user scrolls 50% down
    const handleScroll = () => {
      try {
        const dismissed = localStorage.getItem('leadPopupDismissed');
        if (dismissed) return;
        const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
        if (scrollPercent > 0.5 && !show) {
          setShow(true);
          trackLeadFunnel('popup_shown', 'scroll_trigger');
        }
      } catch (e) {}
    };
    window.addEventListener('scroll', handleScroll);

    // Listen for explicit trigger (e.g. from navbar "Get Free Kit")
    const handleOpen = () => {
      setShow(true);
      trackLeadFunnel('popup_shown', 'explicit_click');
      try { localStorage.removeItem('leadPopupDismissed'); } catch (e) {}
    };
    window.addEventListener('openLeadPopup', handleOpen);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('openLeadPopup', handleOpen);
    };
  }, [show]);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    setError('');

    try {
      const res = await captureLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        source: 'website_popup_whatsapp',
        campaign: 'free_resources_whatsapp',
      });

      if (res.success) {
        trackLeadFunnel('success', 'website_popup_whatsapp', { email: form.email });
        setSubmitted(true);
        setTimeout(() => {
          setShow(false);
          try { localStorage.setItem('leadPopupDismissed', 'true'); } catch (e) {}
        }, 3000);
      } else {
        setError(res.message || 'Something went wrong. Please try again.');
      }
    } catch (err: any) {
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => {
    trackLeadFunnel('popup_dismissed', 'website_popup_whatsapp');
    setShow(false);
    try { localStorage.setItem('leadPopupDismissed', 'true'); } catch (e) {}
  };

  if (mounted && !show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={dismiss}
        className="fixed inset-0 bg-slate-900/60 z-[100] backdrop-blur-sm"
      />

      {/* Popup Container */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-[101] px-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {submitted ? (
            /* ─── Success State ─── */
            <div className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">You're In!</h3>
              <p className="text-slate-600 mb-2">
                Check your WhatsApp — your free AI Finance Starter Kit is on its way.
              </p>
              <p className="text-sm text-slate-400">
                We've also sent a confirmation to <strong>{form.email}</strong>
              </p>
            </div>
          ) : (
            <>
              {/* ─── Top Banner ─── */}
              <div className="bg-amber-500 px-6 py-3 flex items-center justify-center gap-2">
                <span className="text-white text-sm font-semibold">
                  Limited Time — Free for First 500 People
                </span>
              </div>

              {/* ─── Header ─── */}
              <div className="px-8 pt-8 pb-4 text-center relative">
                <button
                  onClick={dismiss}
                  className="absolute top-4 right-4 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>

                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Get Your Free AI Starter Kit on WhatsApp
                </h2>
                <p className="text-slate-500 text-base">
                  Join <strong>2,000+</strong> Indian finance professionals. Delivered instantly on WhatsApp.
                </p>
              </div>

              {/* ─── Benefits ─── */}
              <div className="px-8 pb-6">
                <div className="bg-amber-50 rounded-xl p-4 space-y-3">
                  {BENEFITS.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ─── Form ─── */}
              <div className="px-8 pb-8 space-y-4">
                <Input
                  label="Full Name"
                  placeholder="e.g. Rahul Sharma"
                  icon={<User className="w-4 h-4" />}
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  required
                />

                <Input
                  label="Work Email"
                  type="email"
                  placeholder="rahul@company.com"
                  icon={<Mail className="w-4 h-4" />}
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                />

                <Input
                  label="WhatsApp Number"
                  type="tel"
                  placeholder="+91 98765 43210"
                  icon={<Phone className="w-4 h-4" />}
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  required
                  helper="We'll send your starter kit here"
                />

                {error && (
                  <p className="text-sm text-red-500 text-center">{error}</p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading || !form.name.trim() || !form.email.trim() || !form.phone.trim()}
                  className="w-full py-3.5 px-6 bg-green-500 hover:bg-green-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold text-base rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-5 h-5" />
                      Send to My WhatsApp
                    </>
                  )}
                </button>

                {/* Trust badges */}
                <div className="flex items-center justify-center gap-4 pt-2">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <Shield className="w-3.5 h-3.5" />
                    100% Secure
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <Users className="w-3.5 h-3.5" />
                    No Spam Ever
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
